import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { MOODS } from "@/data/parentingData";
import { useChildren } from "@/context/ChildrenContext";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2, ImagePlus, X } from "lucide-react";

// Compress image to <=1MB base64 dataURL, max 1200px longest side
async function fileToCompressedDataUrl(file, maxSide = 1200, quality = 0.8) {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale);
  const h = Math.round(bmp.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

export default function JournalPage() {
  const { activeChild } = useChildren();
  const fileInput = useRef(null);
  const [entries, setEntries] = useState([]);
  const [parentMood, setParentMood] = useState("calme");
  const [childMood, setChildMood] = useState("joyeux");
  const [note, setNote] = useState("");
  const [tag, setTag] = useState("");
  const [photo, setPhoto] = useState(null);
  const [view, setView] = useState("timeline"); // timeline | photos

  const load = async () => {
    const { data } = await api.get("/journal");
    setEntries(data);
  };
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Photo trop lourde (max 8 Mo)");
      return;
    }
    try {
      const dataUrl = await fileToCompressedDataUrl(f);
      setPhoto(dataUrl);
    } catch {
      toast.error("Impossible de lire la photo");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    await api.post("/journal", {
      child_id: activeChild?.id,
      parent_mood: parentMood,
      child_mood: childMood,
      note,
      tags: tag ? tag.split(",").map((t) => t.trim()).filter(Boolean) : [],
      photo,
    });
    setNote(""); setTag(""); setPhoto(null);
    if (fileInput.current) fileInput.current.value = "";
    toast.success("Moment enregistré 📖");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/journal/${id}`);
    toast.success("Entrée supprimée");
    load();
  };

  const withPhotos = entries.filter((e) => e.photo);

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="journal-tracker-section">
      <section className="soft-card p-6 sm:p-8 space-y-5">
        <div>
          <h2 className="text-2xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>Nouvelle entrée</h2>
          <p className="mt-1 text-[#77849C] text-sm">Un moment, une émotion, une leçon.</p>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-[#77849C] mb-2">Ton humeur</p>
          <div className="flex flex-wrap gap-2">
            {MOODS.map((m) => (
              <button key={m.key} type="button" data-testid={`parent-mood-${m.key}`} onClick={() => setParentMood(m.key)}
                style={{ backgroundColor: parentMood === m.key ? m.color : "#FFF" }}
                className={`rounded-full px-3 py-1.5 text-sm border transition ${parentMood === m.key ? "border-[#23375E]" : "border-[#E3E8F1]"}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-[#77849C] mb-2">
            Humeur de {activeChild?.name || "l'enfant"}
          </p>
          <div className="flex flex-wrap gap-2">
            {MOODS.map((m) => (
              <button key={m.key} type="button" data-testid={`child-mood-${m.key}`} onClick={() => setChildMood(m.key)}
                style={{ backgroundColor: childMood === m.key ? m.color : "#FFF" }}
                className={`rounded-full px-3 py-1.5 text-sm border transition ${childMood === m.key ? "border-[#23375E]" : "border-[#E3E8F1]"}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={save} className="space-y-3">
          <Textarea data-testid="journal-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ce qui s'est passé aujourd'hui..." rows={4} required />
          <input data-testid="journal-tag-input" value={tag} onChange={(e) => setTag(e.target.value)} placeholder="tags: école, coucher, câlin (séparés par ,)" className="w-full rounded-xl border border-[#E3E8F1] bg-white px-4 py-2 text-sm" />

          <div>
            <input
              ref={fileInput}
              data-testid="photo-input"
              type="file"
              accept="image/*"
              onChange={onFile}
              className="hidden"
            />
            {photo ? (
              <div className="relative inline-block">
                <img src={photo} alt="preview" className="h-24 rounded-xl object-cover" />
                <button
                  type="button"
                  data-testid="remove-photo-btn"
                  onClick={() => { setPhoto(null); if (fileInput.current) fileInput.current.value = ""; }}
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white shadow grid place-items-center"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="add-photo-btn"
                onClick={() => fileInput.current?.click()}
                className="rounded-full border-2 border-dashed border-[#E3E8F1] px-4 py-2 text-sm text-[#77849C] hover:border-[#23375E] hover:text-[#23375E] transition inline-flex items-center gap-2"
              >
                <ImagePlus className="h-4 w-4" /> Joindre une photo
              </button>
            )}
          </div>

          <Button data-testid="save-journal-btn" type="submit" className="brand-btn rounded-full px-5">Enregistrer</Button>
        </form>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
            {view === "timeline" ? "Timeline" : "Galerie photos"}
          </h2>
          <div className="flex bg-[#EDF2F9] p-1 rounded-full">
            <button
              data-testid="view-timeline"
              onClick={() => setView("timeline")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${view === "timeline" ? "bg-white text-[#16233D] shadow-sm" : "text-[#77849C]"}`}
            >
              Timeline
            </button>
            <button
              data-testid="view-photos"
              onClick={() => setView("photos")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${view === "photos" ? "bg-white text-[#16233D] shadow-sm" : "text-[#77849C]"}`}
            >
              Photos ({withPhotos.length})
            </button>
          </div>
        </div>

        {view === "timeline" ? (
          <>
            {entries.length === 0 && <p className="text-[#77849C]">Aucune entrée pour le moment.</p>}
            {entries.map((e) => {
              const pm = MOODS.find((m) => m.key === e.parent_mood);
              const cm = MOODS.find((m) => m.key === e.child_mood);
              return (
                <article key={e.id} data-testid={`entry-${e.id}`} className="soft-card p-5">
                  <div className="flex justify-between items-start gap-2">
                    <div className="text-xs text-[#77849C]">
                      {new Date(e.created_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                    <button data-testid={`del-${e.id}`} onClick={() => remove(e.id)} className="text-[#77849C] hover:text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <span className="age-badge" style={{ backgroundColor: pm?.color }}>Parent {pm?.label}</span>
                    <span className="age-badge" style={{ backgroundColor: cm?.color }}>Enfant {cm?.label}</span>
                  </div>
                  {e.photo && (
                    <img src={e.photo} alt="souvenir" className="mt-3 w-full max-h-80 object-cover rounded-2xl" />
                  )}
                  <p className="mt-3 text-[#16233D] leading-relaxed">{e.note}</p>
                  {e.tags?.length > 0 && (
                    <div className="mt-2 flex gap-1 flex-wrap">
                      {e.tags.map((t) => (
                        <span key={t} className="text-xs bg-[#EDF2F9] text-[#16233D] rounded-full px-2 py-0.5">#{t}</span>
                      ))}
                    </div>
                  )}
                </article>
              );
            })}
          </>
        ) : (
          <>
            {withPhotos.length === 0 && (
              <p className="text-[#77849C]">Aucune photo pour l'instant. Ajoute-en depuis une entrée journal.</p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {withPhotos.map((e) => (
                <div key={e.id} data-testid={`photo-item-${e.id}`} className="relative group rounded-2xl overflow-hidden">
                  <img src={e.photo} alt="souvenir" className="w-full aspect-square object-cover" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-white text-xs">
                      {new Date(e.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
